import { useState, useMemo, useCallback, useEffect } from "react";
import { createActionHandlers } from "./actionHandlers.js";
import { createCellRenderer } from "./cellRenderers.js";
import api from "../../../services/api.js";
import config from "../../../config.js";
import { Toast } from "../../../shared/Toast.js";
import createdSVG from "../../../assets/images/created.svg"
import runningSVG from "../../../assets/images/running.svg"
import pausedSVG from "../../../assets/images/paused.svg"
import completedSVG from "../../../assets/images/completed.svg"
import scheduledSVG from "../../../assets/images/scheduled.svg"

export const LINE_ITEM_STATUS =  [
    { value: 1, label: "Created", icon: createdSVG },
    { value: 2, label: "Running", icon: runningSVG },
    { value: 3, label: "Paused", icon: pausedSVG },
    { value: 4, label: "Completed", icon: completedSVG },
    { value: 5, label: "Scheduled", icon: scheduledSVG },
    { value: 6, label: "Approved", icon: ""}
]
export const useTableState = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [adsData, setAdsData] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [designers, setDesigners] = useState([]);
    const [contentWriters, setContentWriters] = useState([]);
    const [filters, setFilters] = useState({});
    const [salesList, setSalesList] = useState([]);
    const [salesLocations, setLocations] = useState([]);
    const [adCounts, setAdCounts] = useState({
        total: 0,
        pending_ads: 0,
        ready_to_live: 0,
        completed_ads: 0,
        myads: 0,
        unassigned: 0,
        unpicked_ready_to_live: 0,
        unpicked: 0,
        payment_completed: 0,
    });

    const [showActionsMenu, setShowActionsMenu] = useState(-1);
    const [modal, setModal] = useState({ open: false, type: null, image: null, timelineData: null });
    const [drawer, setDrawer] = useState({ open: false, type: null, rowData: null });
    const [expandedVersionsRow, setExpandedVersionsRow] = useState(null);
    const [goLiveLoadingIdx, setGoLiveLoadingIdx] = useState(null);

    const actionHandlers = useMemo(
        () =>
            createActionHandlers({
                setModal,
                setDrawer,
                setExpandedVersionsRow,
                setShowActionsMenu,
            }),
        [],
    );

    const renderCell = useMemo(() => createCellRenderer(), []);

    // Generic data fetching functions

    const fetchAdCounts = async (data) => {
        const res = await api({
            url: `/alpha_cst_designer/getAdCount`,
            method: "POST",
            responseType: "json",
            data
        });
        if (res.code === 200) {
            setAdCounts(res.data.data);
        }
    };

    const fetchAds = useCallback(
        async (endpoint, page = 0, isActive = true, filtersArg = {}, appendData = false) => {
            if (!isActive) return;
            setLoading(true);
            try {
                const res = await api({
                    url: endpoint,
                    method: "POST",
                    responseType: "json",
                    data: {
                        page: page,
                        limit: config.PER_PAGE,
                        ...filtersArg,
                    },
                });
                if (res.code === 200) {
                    const newData = res.data?.data || [];
                    const total = res.data?.total || 0;
                    if (appendData) {
                        setAdsData(prev => [...prev, ...newData]);
                    } else {
                        setAdsData(newData);
                    }
                    const totalPgs = Math.ceil(total / config.PER_PAGE) || 0;
                    setTotalPages(totalPgs);
                    setTotalRecords(total);
                    setHasMore((page + 1) < totalPgs);
                }
            } catch (error) {
                console.error(`Error fetching ads from ${endpoint}:`, error);
                if (!appendData) setAdsData([]);
                setTotalPages(0);
                setHasMore(false);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    // Designer related functions
    const fetchDesigners = useCallback(async () => {
        try {
            const res = await api({
                url: `/alpha_cst_designer/getDesigners`,
                method: "POST",
                responseType: "json",
                data: {},
            });
            if (res.code === 200) {
                setDesigners(res.data?.data || []);
            }
        } catch (error) {
            console.error("Error fetching designers:", error);
            setDesigners([]);
        }
    }, []);
    const fetchContentWriters = useCallback(async () => {
        try {
            const res = await api({
                url: `/user/content_writer/contentWriters`,
                method: "POST",
                responseType: "json",
                data: {},
            });
            if (res.code === 200) {
                setContentWriters(res.data || []);
            }
        } catch (error) {
            console.error("Error fetching designers:", error);
            setContentWriters([]);
        }
    }, []);

    const fetchSalesLocations = async () => {
        const result = await api({
            url: `/alpha_cst_designer/get_alpha_sales_districts`,
            method: "POST",
            responseType: "json",
            data: {},
        });
        if (result.status === "SUCCESS") {
            setLocations(result.data || []);
        }
    };

    // Specific fetch functions for different ad types
    const fetchPendingAds = useCallback(
        (page = 0, isActive = true, filtersArg = {}, appendData = false) => {
            return fetchAds("/alpha_cst_designer/my-ads-pending", page, isActive, filtersArg, appendData);
        },
        [fetchAds],
    );

    const fetchReadyToLiveAds = useCallback(
        (page = 0, isActive = true, filtersArg = {}, appendData = false) => {
            return fetchAds("/alpha_cst_designer/my-ads-ready_to_live", page, isActive, filtersArg, appendData);
        },
        [fetchAds],
    );

    const fetchCompletedAds = useCallback(
        (page = 0, isActive = true, filtersArg = {}, appendData = false) => {
            return fetchAds("/alpha_cst_designer/my-ads-completed", page, isActive, filtersArg, appendData);
        },
        [fetchAds],
    );

    const fetchAllAdsForCsv = useCallback(
        async (endpoint, filtersArg = {}) => {
            try {
                const res = await api({
                    url: endpoint,
                    method: "POST",
                    responseType: "json",
                    data: {
                        page: 0,
                        limit: totalRecords,
                        ...filtersArg,
                    },
                });
                if (res.code === 200) {
                    return res.data?.data || [];
                }
                return [];
            } catch (error) {
                console.error(`Error fetching all ads from ${endpoint}:`, error);
                return [];
            }
        },
        [totalRecords],
    );

    const fetchAllAds = useCallback(
        (page = 0, isActive = true, filtersArg = {}, appendData = false) => {
            return fetchAds("/alpha_cst_designer/all-ads", page, isActive, filtersArg, appendData);
        },
        [fetchAds],
    );

    const handleDesignerChange = useCallback(
        async (creativeId, designerId, designerName, creativeName, onSuccess = fetchPendingAds) => {
            if (!designerId) {
                Toast("warning", "Please select a designer.");
                return;
            }
            setLoading(true);
            try {
                const res = await api({
                    url: "/alpha_cst_designer/changeDesigner",
                    method: "POST",
                    responseType: "json",
                    data: {
                        creative_id: creativeId,
                        designer_id: parseInt(designerId),
                        designer_name: designerName,
                        creative_name: creativeName
                    },
                });
                if (res.code === 200) {
                    Toast("success", "Designer assigned successfully");
                    onSuccess();
                    return res;
                } else {
                    Toast("error", res.data?.message || "Failed to assign designer");
                    return false;
                }
            } catch (error) {
                Toast("error", "Error assigning designer");
                return false;
            } finally {
                setLoading(false);
            }
        },
        [designers, fetchPendingAds, currentPage],
    );

    const handleContentWriterChange = useCallback(
        async (content_id, creativeId, contentWriterId, contentWriterName, description, onSuccess = fetchPendingAds) => {
            if (!contentWriterId) {
                Toast("warning", "Please select a content writer.");
                return;
            }
            setLoading(true);
            try {
                const res = await api({
                    url: "/alpha_cst_designer/contentWriter",
                    method: "POST",
                    responseType: "json",
                    data: {
                        content_id: content_id || null,
                        creative_id: creativeId,
                        content_writer_id: parseInt(contentWriterId) || null,
                        content_writer_name: contentWriterName || null,
                        description: description,
                    },
                });
                if (res.code === 200) {
                    Toast("success", "Content writer assigned successfully");
                    onSuccess();
                    return res;
                } else {
                    Toast("error", res.data?.message || "Failed to assign content writer");
                    return false;
                }
            } catch (error) {
                Toast("error", "Error assigning content writer");
                return false;
            } finally {
                setLoading(false);
            }
        },
        [contentWriters, fetchPendingAds, currentPage],
    );
    const fetchContent = async (creative_id) => {
        if (!creative_id) return [];
        const result = await api({
            url: `/user/content_writer/getContent`,
            method: "POST",
            responseType: "json",
            data: { creative_id },
        });
        return result.data || [];
    };
    const handleGoToLive = useCallback(
        async (creative, creativeId, rowIdx) => {
            setGoLiveLoadingIdx(rowIdx);
            try {
                const res = await api({
                    url: "/alpha_cst_designer/goLive",
                    method: "POST",
                    responseType: "json",
                    data: { creative_id: creativeId },
                });
                if (res.code === 200) {
                    Toast("success", "Ad went live successfully");
                    fetchReadyToLiveAds();
                    return res;
                } else {
                    Toast("error", res.data?.message || "Failed to go live");
                    return false;
                }
            } catch (error) {
                Toast("error", "Error going live");
                return false;
            } finally {
                setGoLiveLoadingIdx(null);
                setLoading(false);
            }
        },
        [fetchReadyToLiveAds],
    );
    const adTypeChange = async (creative_id, ad_creative_sub_type) => {
        try {
            const result = await api({
                url: "/alpha_cst_designer/change_ad_type",
                method: "POST",
                data: {
                    creative_id,
                    ad_creative_sub_type,
                },
            });
            if (result.status === "SUCCESS") {
                Toast("success", "Please Refresh Page, Ad Type Changed Successfully");
            } else Toast("error", "Failed to change ad type");
            return result;
        } catch (error) {
            console.log(error);
            return false;
        }
    };
    const acceptRequest = async (row) => {
        const result = await api({
            url: "/alpha_cst_designer/accept_request",
            method: "POST",
            data: {
                creative_id: row?.creative_id,
            },
        });
        if (result.status === "SUCCESS") Toast("success", "Please Refresh Page, Request accepted successfully");
        else Toast("error", "Failed to accept request");
        return result;
    };
    const rejectRequest = async (row) => {
        const result = await api({
            url: "/alpha_cst_designer/reject_request",
            method: "POST",
            data: {
                creative_id: row?.creative_id,
                creative: row,
            },
        });
        if (result.status === "SUCCESS") Toast("success", "Please Refresh Page, Request Rejected successfully");
        else Toast("error", "Failed to reject request");
        return result;
    };
    const getSalesList = async () => {
        try {
            let salesListResponse = await api({
                url: "/user/sales_persons/getSalesUsers",
                method: "POST",
                responseType: "json",
            });
            if (salesListResponse.code === 200) {
                setSalesList(salesListResponse.data);
            }
        } catch (error) {
            console.log("Error @ getSalesList", error);
        }
    };
    // Page change handlers
    const handlePageChange = (event)=>{
        const newPage = event.selected;
        setCurrentPage(newPage);
    }
    const handlePageClick = (event) => {
        const newPage = event.selected;
        setCurrentPage(newPage);
        setExpandedVersionsRow(null);
        setShowActionsMenu(-1);
    }

    // UI utility functions
    const getModalAlt = useCallback((type) => {
        return type === 'latestDesign' ? 'Latest Design' : 'Timeline';
    }, []);

    const closeExpandedRows = useCallback(() => {
        setExpandedVersionsRow(null);
        setShowActionsMenu(-1);
    }, []);


    return {
        // State
        currentPage,
        loading,
        totalPages,
        adsData,
        hasMore,
        designers,
        adCounts,
        showActionsMenu,
        modal,
        drawer,
        expandedVersionsRow,
        filters,
        goLiveLoadingIdx,
        contentWriters,
        salesList,
        salesLocations,
        totalRecords,
        // Data fetching functions
        fetchAds,
        fetchPendingAds,
        fetchReadyToLiveAds,
        fetchCompletedAds,
        fetchAllAds,
        fetchAllAdsForCsv,
        fetchDesigners,
        fetchAdCounts,
        fetchContentWriters,
        fetchContent,

        // Handlers
        actionHandlers,
        renderCell,
        handlePageClick,
        handlePageChange,
        getModalAlt,
        closeExpandedRows,
        handleDesignerChange,
        handleContentWriterChange,
        handleGoToLive,
        adTypeChange,
        acceptRequest,
        rejectRequest,
        // Setters
        setCurrentPage,
        setAdCounts,
        setShowActionsMenu,
        setModal,
        setDrawer,
        setExpandedVersionsRow,
        setAdsData,
        setTotalPages,
        setLoading,
        setDesigners,
        setFilters,
        setContentWriters,
        getSalesList,
        fetchSalesLocations
    };
}; 